import React from 'react';

import DataConnector from 'connect/DataConnector';
import {BioentityAliasInfo} from 'constants/RegistryConstants';
import * as BioentityActions from 'inventory/BioentityActions';
import InventoryFileStore from 'inventory/InventoryFileStore';
import * as InventoryFileUtil from 'inventory/InventoryFileUtil';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import {ButtonStyle} from 'ui-library/buttons/Button';
import * as ModalContainer from 'ui-library/ModalContainer';
import Modal from 'ui-library/modals/Modal';
import Spinner from 'ui-library/Spinner';

export interface FileInfo {
  id: string;
  name: string;
}

interface OpenSetCollectionModalProps {
  files: Array<FileInfo>;
}

function mapToInPersonalCollections(
  allBioentityAliasInfos: Map<string, Array<BioentityAliasInfo>> | null
): Array<boolean> {
  const personalCollections: Array<boolean> = [];
  allBioentityAliasInfos?.forEach((bioentityAliasInfo) => {
    personalCollections.push(InventoryFileUtil.hasPersonalCollection(bioentityAliasInfo));
  });
  return personalCollections;
}

function mapToInOrgCollections(
  allBioentityAliasInfos: Map<string, Array<BioentityAliasInfo>> | null
): Array<boolean> {
  const orgCollections: Array<boolean> = [];
  allBioentityAliasInfos?.forEach((aliases) => {
    orgCollections.push(InventoryFileUtil.hasOrgCollection(aliases));
  });
  return orgCollections;
}

function areAllFilesInPersonalCollections(
  allBioentityAliasInfos: Map<string, Array<BioentityAliasInfo>> | null
): boolean {
  return mapToInPersonalCollections(allBioentityAliasInfos).every(
    (isInPersonalCollection) => isInPersonalCollection
  );
}

function areAllFilesInOrgCollections(
  allBioentityAliasInfos: Map<string, Array<BioentityAliasInfo>> | null
): boolean {
  return mapToInOrgCollections(allBioentityAliasInfos).every((isInOrgCollection) => isInOrgCollection);
}

function arePersonalCollectionsEqual(
  allBioentityAliasInfos: Map<string, Array<BioentityAliasInfo>> | null
): boolean {
  const personalCollections = mapToInPersonalCollections(allBioentityAliasInfos);
  return personalCollections.every(
    (isInPersonalCollection) => isInPersonalCollection === personalCollections[0]
  );
}

function areOrgCollectionsEqual(
  allBioentityAliasInfos: Map<string, Array<BioentityAliasInfo>> | null
): boolean {
  const orgCollections = mapToInOrgCollections(allBioentityAliasInfos);
  return orgCollections.every((isInOrgCollection) => isInOrgCollection === orgCollections[0]);
}

export function openSetCollectionModal(props: OpenSetCollectionModalProps): void {
  const onRequestClose = (): void => {
    ModalContainer.close(SetCollectionModal);
  };
  ModalContainer.open(
    SetCollectionModal,
    <ConnectedSetCollectionModal {...props} onRequestClose={onRequestClose} />
  );
}

export interface SetCollectionModalConnectorProps {
  files: Array<FileInfo>;
  onRequestClose: () => void;
}

interface SetCollectionModalConnectorEnhancedProps {
  bioentityAliasInfos: Map<string, Array<BioentityAliasInfo>> | null;
}

class SetCollectionModalConnector extends DataConnector<
  SetCollectionModalConnectorProps,
  SetCollectionModalConnectorEnhancedProps
> {
  override shouldSubscribeToRedux = false;
  override stores = [InventoryFileStore];

  enhanceProps(): SetCollectionModalConnectorEnhancedProps {
    const bioentityAliasInfos = new Map<string, Array<BioentityAliasInfo>>();
    this.props.files.forEach(({id: fileId}) => {
      const aliases = this.inventoryFileStore().getBioentityAliasInfos(fileId);
      if (aliases) {
        bioentityAliasInfos.set(fileId, aliases);
      }
    });
    if (bioentityAliasInfos.size !== this.props.files.length) {
      return {bioentityAliasInfos: null};
    }
    return {bioentityAliasInfos};
  }
}

interface SetCollectionModalProps
  extends SetCollectionModalConnectorProps, SetCollectionModalConnectorEnhancedProps {}

interface SetCollectionModalState {
  isPersonalCollection: boolean;
  isOrgCollection: boolean;
}

class SetCollectionModal extends React.Component<SetCollectionModalProps, SetCollectionModalState> {
  override state: SetCollectionModalState = {
    isPersonalCollection: true,
    isOrgCollection: true,
  };

  override componentDidMount(): void {
    if (!this.props.bioentityAliasInfos) {
      BioentityActions.loadBioentityAliasInfos(this.props.files.map(({id}) => id));
    }
    this.setState(this.getStateFromAliases());
  }

  override componentDidUpdate(prevProps: SetCollectionModalProps): void {
    if (prevProps.bioentityAliasInfos !== this.props.bioentityAliasInfos) {
      this.setState(this.getStateFromAliases());
    }
  }

  private getStateFromAliases = (): SetCollectionModalState => ({
    isPersonalCollection: areAllFilesInPersonalCollections(this.props.bioentityAliasInfos),
    isOrgCollection: areAllFilesInOrgCollections(this.props.bioentityAliasInfos),
  });

  private hasCollection = (): boolean => this.state.isPersonalCollection || this.state.isOrgCollection;

  private enableSubmit = (): boolean =>
    this.props.bioentityAliasInfos != null &&
    this.props.bioentityAliasInfos &&
    this.hasCollection() &&
    this.allCollectionsMatch();

  private allCollectionsMatch = (): boolean => {
    if (!this.props.bioentityAliasInfos) {
      return false;
    }
    return (
      arePersonalCollectionsEqual(this.props.bioentityAliasInfos) &&
      areOrgCollectionsEqual(this.props.bioentityAliasInfos)
    );
  };

  private renderFilesWarning = (): React.ReactNode => {
    if (this.props.files.length === 0 || !this.props.bioentityAliasInfos) {
      return null;
    }

    if (this.allCollectionsMatch()) {
      return null;
    }

    return (
      <span className="u-danger-color u-tiny-spacing-left">
        Files must be in the same collections in order to bulk add or remove.
      </span>
    );
  };

  private renderCollectionWarning = (): React.ReactNode => {
    if (this.hasCollection()) {
      return null;
    }
    return <span className="u-danger-color u-tiny-spacing-left">Please select at least one collection</span>;
  };

  private setCollections = (): void => {
    const fileIds = this.props.files.map(({id}) => id);
    BioentityActions.updateRegistryCollections(fileIds, {
      inOrgCollection: this.state.isOrgCollection,
      inPersonalCollection: this.state.isPersonalCollection,
    });
    this.props.onRequestClose();
  };

  private renderModalContents = (): React.ReactNode => (
    <div>
      <div>
        <label>Files</label>
        {this.renderFilesWarning()}
        <div>{this.props.files.map(({name}) => name).join(', ')}</div>
      </div>
      <div className="u-spacing-top">
        <label>Collection</label>
        {this.renderCollectionWarning()}
        <Row className="u-tiny-spacing-top">
          <Col sm={6}>
            <label className="u-normal-font-weight">
              <input
                autoFocus={true}
                checked={this.state.isPersonalCollection}
                onChange={(e) => {
                  this.setState({isPersonalCollection: e.target.checked});
                }}
                type="checkbox"
              />
              Personal Collection
            </label>
          </Col>
          <Col sm={6}>
            <label className="u-normal-font-weight">
              <input
                type="checkbox"
                checked={this.state.isOrgCollection}
                onChange={(e) => {
                  this.setState({isOrgCollection: e.target.checked});
                }}
              />
              Organization Collection
            </label>
          </Col>
        </Row>
      </div>
    </div>
  );

  override render(): React.ReactNode {
    return (
      <Modal
        title="Change collection"
        onRequestClose={this.props.onRequestClose}
        buttons={[
          {
            disabled: !this.enableSubmit(),
            key: 'submit',
            onClick: this.setCollections,
            style: ButtonStyle.PRIMARY,
            submit: true,
            text: 'Save',
          },
        ]}
      >
        {this.props.bioentityAliasInfos ? (
          this.renderModalContents()
        ) : (
          <div className="loading-spinner-background u-small-padding">
            <Spinner />
          </div>
        )}
      </Modal>
    );
  }
}

const ConnectedSetCollectionModal = SetCollectionModalConnector.connect(SetCollectionModal);
export default ConnectedSetCollectionModal;
